/**
 * Products Redux slice
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Product, ProductFilters, AsyncState } from '@/types';

interface ProductsState {
  items: Product[];
  featuredProducts: Product[];
  selectedProduct: Product | null;
  filters: ProductFilters;
  loading: AsyncState;
  error: string | null;
  totalCount: number;
  hasMore: boolean;
}

const initialState: ProductsState = {
  items: [],
  featuredProducts: [],
  selectedProduct: null,
  filters: {},
  loading: 'idle',
  error: null,
  totalCount: 0,
  hasMore: true,
};

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    /**
     * Set products list
     */
    setProducts: (state, action: PayloadAction<Product[]>) => {
      state.items = action.payload;
      state.loading = 'succeeded';
    },

    /**
     * Append products (for infinite scroll)
     */
    appendProducts: (state, action: PayloadAction<Product[]>) => {
      state.items = [...state.items, ...action.payload];
    },

    /**
     * Set featured products
     */
    setFeaturedProducts: (state, action: PayloadAction<Product[]>) => {
      state.featuredProducts = action.payload;
    },

    /**
     * Set selected product
     */
    setSelectedProduct: (state, action: PayloadAction<Product | null>) => {
      state.selectedProduct = action.payload;
    },

    /**
     * Set filters
     */
    setFilters: (state, action: PayloadAction<ProductFilters>) => {
      state.filters = action.payload;
    },

    /**
     * Update filters
     */
    updateFilters: (state, action: PayloadAction<Partial<ProductFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    /**
     * Clear filters
     */
    clearFilters: (state) => {
      state.filters = {};
    },

    /**
     * Set loading state
     */
    setLoading: (state, action: PayloadAction<AsyncState>) => {
      state.loading = action.payload;
    },

    /**
     * Set error
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = 'failed';
    },

    /**
     * Set total count
     */
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },

    /**
     * Set has more flag
     */
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },

    /**
     * Clear products
     */
    clearProducts: (state) => {
      state.items = [];
      state.totalCount = 0;
      state.hasMore = true;
    },
  },
});

// Export actions
export const {
  setProducts,
  appendProducts,
  setFeaturedProducts,
  setSelectedProduct,
  setFilters,
  updateFilters,
  clearFilters,
  setLoading,
  setError,
  setTotalCount,
  setHasMore,
  clearProducts,
} = productsSlice.actions;

// Selectors
export const selectProducts = (state: { products: ProductsState }) =>
  state.products.items;

export const selectFeaturedProducts = (state: { products: ProductsState }) =>
  state.products.featuredProducts;

export const selectSelectedProduct = (state: { products: ProductsState }) =>
  state.products.selectedProduct;

export const selectFilters = (state: { products: ProductsState }) =>
  state.products.filters;

export const selectProductsLoading = (state: { products: ProductsState }) =>
  state.products.loading;

export const selectProductsError = (state: { products: ProductsState }) =>
  state.products.error;

export const selectTotalCount = (state: { products: ProductsState }) =>
  state.products.totalCount;

export const selectHasMore = (state: { products: ProductsState }) =>
  state.products.hasMore;

export default productsSlice.reducer;
